import { useTranslation } from 'react-i18next';
import { formatDate, type Langue } from '@artosera/shared';
import type { OffrePresentee } from '../../api/offres.js';
import { friseDeLOffre, type EvenementFrise } from './syntheseOffre.js';
import styles from './FriseOffre.module.css';

const jour = (valeur: string, langue: Langue) =>
  formatDate(new Date(valeur.length <= 10 ? `${valeur}T12:00:00Z` : valeur), langue);

/**
 * La négociation dans l'ordre : ce qui s'est passé, puis ce qui reste à venir.
 * Une offre porte un seul prix par œuvre (la contre-proposition est une nouvelle
 * offre) : la frise suit donc les envois, les relances et la réponse du contact.
 */
export function FriseOffre({ offre, langue }: { offre: OffrePresentee; langue: Langue }) {
  const { t } = useTranslation('regie');
  const { passes, aVenir } = friseDeLOffre(offre);
  const ligne = (evenement: EvenementFrise, avenir: boolean) => {
    const cle = `offres.frise.${evenement.cle}`;
    let titre: string;
    let precision = '';
    let ton: 'fait' | 'alerte' | 'attente' = avenir ? 'attente' : 'fait';
    switch (evenement.cle) {
      case 'envoi':
      case 'relance':
        titre = t(cle, { destinataire: evenement.destinataire });
        precision = t(`offres.fiche.resultats.${evenement.resultat}`, {
          defaultValue: evenement.resultat,
        });
        if (evenement.resultat === 'echec') ton = 'alerte';
        break;
      case 'envoyee':
        titre = t(`${cle}.${evenement.canal ?? 'courriel'}`);
        break;
      case 'relancePrevue':
        titre = t(evenement.due ? `${cle}Due` : cle);
        if (evenement.due) ton = 'alerte';
        break;
      case 'echeance':
        titre = t(evenement.passee ? `${cle}Passee` : cle);
        if (evenement.passee) ton = 'alerte';
        break;
      case 'reservation':
        titre = t(evenement.fait ? `${cle}Faite` : cle, {
          reservees: evenement.reservees,
          total: evenement.total,
        });
        if (evenement.fait) ton = 'fait';
        break;
      case 'facture':
        titre = t(evenement.fait ? `${cle}Faite` : cle);
        if (evenement.fait) ton = 'fait';
        break;
      case 'acceptee':
      case 'refusee':
      case 'retiree':
      case 'expiree':
        titre = t(cle);
        precision = evenement.motif;
        if (evenement.cle !== 'acceptee') ton = 'alerte';
        break;
      default:
        titre = t(cle);
    }
    const date = 'date' in evenement ? evenement.date : null;
    return (
      <li key={`${evenement.cle}-${date ?? ''}-${titre}`} className={styles.etape} data-ton={ton}>
        <span className={styles.titre}>{titre}</span>
        {date ? (
          <time className={styles.date} dateTime={date}>
            {jour(date, langue)}
          </time>
        ) : null}
        {precision ? <span className={styles.precision}>{precision}</span> : null}
      </li>
    );
  };
  return (
    <ol className={styles.frise} aria-label={t('offres.frise.titre')}>
      {passes.map((e) => ligne(e, false))}
      {aVenir.map((e) => ligne(e, true))}
    </ol>
  );
}
